/**
 * Live-таймлайн фаз обработки видео.
 *
 * Опрашивает GET /admin/module-items/:id/video-state раз в секунду пока есть
 * активные фазы, иначе раз в 5 секунд. Показывает прогресс/стейдж/деталь,
 * позволяет отменить активную фазу (Cancel-кнопка → POST /video-state/cancel).
 *
 * Один компонент покрывает: импорт с Яндекса, импорт с Google Drive, HLS-
 * обработку, извлечение постера. Идея — один экран для всей жизни видео.
 */

import { useCallback, useEffect, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle2,
  AlertCircle,
  Loader2,
  Clock,
  Circle,
  Ban,
  X,
  Play,
  Shield,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export type PhaseKey = 'import' | 'hls' | 'poster';
export type PhaseStatus = 'idle' | 'queued' | 'running' | 'success' | 'failed' | 'cancelled';

export interface PhaseSnapshot {
  key: PhaseKey;
  label: string;
  status: PhaseStatus;
  progress: number | null;
  stage: string | null;
  detail: string | null;
  error: string | null;
  source: 'gdrive' | 'yandex' | 'upload' | null;
  cancellable: boolean;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface VideoStateSnapshot {
  moduleItemId: number;
  phases: PhaseSnapshot[];
  videoUrl: string | null;
  hlsPlaylistUrl: string | null;
  posterUrl: string | null;
  posterFileId: number | null;
  isProtected: boolean;
  anyActive: boolean;
}

interface VideoStateTimelineProps {
  moduleItemId: string;
  /** Колбэк после успешной отмены — родитель может перезапросить moduleItem. */
  onCancelled?: () => void;
  /** Колбэк когда HLS перешёл в READY — родитель обновит данные урока. */
  onReady?: () => void;
  /** Каждый раз когда приходит свежий snapshot — родитель видит import-фазу. */
  onSnapshot?: (snapshot: VideoStateSnapshot) => void;
  className?: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T | null;
  errors: string[] | null;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isVideoStateSnapshot(value: unknown): value is VideoStateSnapshot {
  if (!isPlainObject(value)) return false;
  return Array.isArray(value.phases) && typeof value.moduleItemId === 'number';
}

function isApiEnvelope(value: unknown): value is ApiEnvelope<unknown> {
  if (!isPlainObject(value)) return false;
  return typeof value.success === 'boolean';
}

const PHASE_ICON: Record<PhaseStatus, typeof Circle> = {
  idle: Circle,
  queued: Clock,
  running: Loader2,
  success: CheckCircle2,
  failed: AlertCircle,
  cancelled: Ban,
};

const PHASE_COLOR: Record<PhaseStatus, string> = {
  idle: 'text-muted-foreground/40',
  queued: 'text-amber-600 dark:text-amber-400',
  running: 'text-blue-600 dark:text-blue-400',
  success: 'text-green-600 dark:text-green-400',
  failed: 'text-destructive',
  cancelled: 'text-muted-foreground',
};

const STATUS_LABEL: Record<PhaseStatus, string> = {
  idle: 'Ожидает',
  queued: 'В очереди',
  running: 'Выполняется',
  success: 'Готово',
  failed: 'Ошибка',
  cancelled: 'Отменено',
};

export function VideoStateTimeline({
  moduleItemId,
  onCancelled,
  onReady,
  onSnapshot,
  className,
}: VideoStateTimelineProps) {
  const apiUrl = useApiUrl();
  const [snapshot, setSnapshot] = useState<VideoStateSnapshot | null>(null);
  const [cancellingPhase, setCancellingPhase] = useState<PhaseKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previousHlsStatus, setPreviousHlsStatus] = useState<PhaseStatus | null>(null);

  // Polling loop с динамическим интервалом.
  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const fetchOnce = async (): Promise<void> => {
      try {
        const response = await fetch(`${apiUrl}/admin/module-items/${moduleItemId}/video-state`, {
          credentials: 'include',
        });
        const raw: unknown = await response.json().catch(() => ({}));
        if (!isApiEnvelope(raw)) {
          throw new Error('Некорректный ответ сервера');
        }
        if (!response.ok || !raw.success || raw.data === null) {
          const message =
            raw.errors !== null && raw.errors.length > 0
              ? raw.errors.join(', ')
              : `HTTP ${response.status}`;
          throw new Error(message);
        }
        if (!isVideoStateSnapshot(raw.data)) {
          throw new Error('Неожиданная форма snapshot');
        }
        if (stopped) return;
        setSnapshot(raw.data);
        setError(null);
        onSnapshot?.(raw.data);
      } catch (err) {
        if (stopped) return;
        // Сетевые ошибки не сбрасывают предыдущий snapshot — показываем баннер,
        // но продолжаем polling.
        setError(err instanceof Error ? err.message : 'Ошибка загрузки статуса');
      }
    };

    const schedule = (delay: number): void => {
      timer = setTimeout(() => {
        void fetchOnce().then(() => {
          if (stopped) return;
          const nextDelay = snapshot?.anyActive === true ? 1000 : 5000;
          schedule(nextDelay);
        });
      }, delay);
    };

    void fetchOnce();
    schedule(snapshot?.anyActive === true ? 1000 : 5000);

    return () => {
      stopped = true;
      if (timer !== null) clearTimeout(timer);
    };
    // ВАЖНО: snapshot.anyActive — единственная зависимость которая нам нужна,
    // но React не различает её внутри snapshot — повторный init на каждом
    // изменении snapshot был бы дорогим. Поэтому используем стабильную
    // зависимость и читаем snapshot через closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, moduleItemId]);

  // Триггер onReady когда HLS перешёл из running/queued в success.
  useEffect(() => {
    if (!snapshot) return;
    const hls = snapshot.phases.find((p) => p.key === 'hls');
    if (!hls) return;
    if (
      previousHlsStatus !== null &&
      previousHlsStatus !== 'success' &&
      hls.status === 'success'
    ) {
      onReady?.();
    }
    setPreviousHlsStatus(hls.status);
  }, [snapshot, previousHlsStatus, onReady]);

  const handleCancel = useCallback(
    async (phase: PhaseKey): Promise<void> => {
      setCancellingPhase(phase);
      setError(null);
      try {
        const response = await fetch(
          `${apiUrl}/admin/module-items/${moduleItemId}/video-state/cancel`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ phase }),
          },
        );
        const raw: unknown = await response.json().catch(() => ({}));
        if (!isApiEnvelope(raw)) {
          throw new Error('Некорректный ответ сервера');
        }
        if (!response.ok || !raw.success) {
          const message =
            raw.errors !== null && raw.errors.length > 0
              ? raw.errors.join(', ')
              : `HTTP ${response.status}`;
          throw new Error(message);
        }
        onCancelled?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Ошибка отмены');
      } finally {
        setCancellingPhase(null);
      }
    },
    [apiUrl, moduleItemId, onCancelled],
  );

  if (snapshot === null) {
    return (
      <div className={cn('flex items-center gap-2 p-4 text-sm text-muted-foreground', className)}>
        <Loader2 className="h-4 w-4 animate-spin" />
        Загрузка состояния…
      </div>
    );
  }

  const phases = snapshot.phases;

  return (
    <div className={cn('space-y-3', className)}>
      {/* Сводный статус */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Состояние видео</span>
          {snapshot.isProtected && (
            <Badge variant="outline" className="gap-1 text-[10px] px-1.5 py-0">
              <Shield className="h-2.5 w-2.5" />
              HLS
            </Badge>
          )}
          {snapshot.hlsPlaylistUrl !== null && (
            <Badge variant="default" className="gap-1 text-[10px] px-1.5 py-0">
              <Play className="h-2.5 w-2.5" />
              Готово к просмотру
            </Badge>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          {snapshot.anyActive ? 'Идёт обработка…' : 'Обновляется автоматически'}
        </span>
      </div>

      {error !== null && (
        <div className="flex items-start gap-2 p-2 rounded-md border border-destructive/50 bg-destructive/5 text-xs text-destructive">
          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Список фаз */}
      <div className="space-y-2">
        {phases.map((phase) => (
          <PhaseRow
            key={phase.key}
            phase={phase}
            cancelling={cancellingPhase === phase.key}
            onCancel={() => {
              void handleCancel(phase.key);
            }}
          />
        ))}
      </div>
    </div>
  );
}

interface PhaseRowProps {
  phase: PhaseSnapshot;
  cancelling: boolean;
  onCancel: () => void;
}

function PhaseRow({ phase, cancelling, onCancel }: PhaseRowProps) {
  const Icon = PHASE_ICON[phase.status];
  const colorClass = PHASE_COLOR[phase.status];
  const isActive = phase.status === 'running' || phase.status === 'queued';
  const showProgress = phase.progress !== null && (phase.status === 'running' || phase.status === 'queued');

  return (
    <div
      className={cn(
        'rounded-md border p-3 transition-colors',
        isActive && 'bg-blue-50/30 border-blue-200/50 dark:bg-blue-950/20 dark:border-blue-800/40',
        phase.status === 'failed' && 'bg-destructive/5 border-destructive/30',
        phase.status === 'success' && 'bg-green-50/30 border-green-200/50 dark:bg-green-950/10 dark:border-green-900/30',
        phase.status === 'idle' && 'opacity-50',
      )}
    >
      <div className="flex items-start gap-2">
        <Icon
          className={cn(
            'h-4 w-4 shrink-0 mt-0.5',
            colorClass,
            phase.status === 'running' && 'animate-spin',
          )}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm font-medium truncate">{phase.label}</span>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">
                {STATUS_LABEL[phase.status]}
              </Badge>
              {phase.stage !== null && (
                <span className="text-xs text-muted-foreground">{phase.stage}</span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {phase.progress !== null && phase.status === 'running' && (
                <span className="text-xs font-mono text-muted-foreground">{phase.progress}%</span>
              )}
              {phase.cancellable && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                  onClick={onCancel}
                  disabled={cancelling}
                >
                  {cancelling ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <X className="h-3 w-3 mr-1" />
                  )}
                  Отменить
                </Button>
              )}
            </div>
          </div>
          {phase.detail !== null && (
            <p className="text-xs text-muted-foreground mt-1">{phase.detail}</p>
          )}
          {phase.error !== null && (
            <p className="text-xs text-destructive mt-1 break-words">{phase.error}</p>
          )}
          {showProgress && (
            <Progress
              value={phase.progress ?? undefined}
              className="h-1.5 mt-2"
            />
          )}
        </div>
      </div>
    </div>
  );
}
